<template>
    <v-container class="grey lighten-5" fluid>
        <v-row>
            <!-- Merchant -->
            <v-col md="2">
                <v-autocomplete
                    v-model="search.merchant"
                    dense
                    :items="merchants"
                    label="Select a merchant"
                    append-icon="mdi-magnify"
                    class="mt-0 pt-0"
                    @change="getChargebacks"
                    clearable
                ></v-autocomplete>
            </v-col>

            <!-- Clearing Institutes -->
            <v-col md="2">
                <v-select
                    v-model="search.clearingInstitute"
                    dense
                    :items="clearingInstitutes"
                    label="Clearing Institutes"
                    append-icon="mdi-magnify"
                    class="mt-0 pt-0"
                    @change="getChargebacks"
                    clearable
                ></v-select>
            </v-col>

            <!-- CBK Type -->
            <v-col md="2">
                <v-select
                    v-model="search.cbkType"
                    dense
                    :items="cbkTypes"
                    label="Chargeback Types"
                    append-icon="mdi-magnify"
                    class="mt-0 pt-0"
                    @change="getChargebacks"
                    clearable
                ></v-select>
            </v-col>

            <!-- ARN -->
            <v-col md="2">
                <v-text-field
                    v-model="search.arn"
                    append-icon="mdi-magnify"
                    label="ARN"
                    clearable
                    class="mt-0 pt-0"
                    @change="getChargebacks"
                    @click:clear="search.arn = null; getChargebacks();"
                ></v-text-field>
            </v-col>

            <!-- UUID -->
            <v-col md="2">
                <v-text-field
                    v-model="search.uuid"
                    append-icon="mdi-magnify"
                    label="UUID"
                    clearable
                    class="mt-0 pt-0"
                    @change="getChargebacks"
                    @click:clear="search.uuid = null; getChargebacks();"
                ></v-text-field>
            </v-col>

            <!-- Represented -->
            <v-col md="2">
                <v-select
                    v-model="search.represented"
                    dense
                    :items="repOptions"
                    label="Represented"
                    append-icon="mdi-magnify"
                    class="mt-0 pt-0"
                    @change="getChargebacks"
                    clearable
                ></v-select>
            </v-col>

            <!-- From Date -->
            <v-col md="2">
                <v-menu
                    v-model="fromDateModal"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                ><template v-slot:activator="{ on }">
                    <v-text-field
                        v-model="search.fromDate"
                        label="From Date"
                        append-icon="mdi-calendar"
                        readonly
                        v-on="on"
                        class="mt-0 pt-0"
                    ></v-text-field
                ></template>
                    <v-date-picker
                        v-model="search.fromDate"
                        @input="fromDateModal = false"
                        @click:date="handleDateFilters"
                    ></v-date-picker>
                </v-menu>
            </v-col>

            <!-- To Date -->
            <v-col md="2">
                <v-menu
                    v-model="toDateModal"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                ><template v-slot:activator="{ on }">
                    <v-text-field
                        v-model="search.toDate"
                        label="To Date"
                        append-icon="mdi-calendar"
                        readonly
                        v-on="on"
                        class="mt-0 pt-0"
                    ></v-text-field
                ></template>
                    <v-date-picker
                        v-model="search.toDate"
                        @input="toDateModal = false"
                        @click:date="handleDateFilters"
                    ></v-date-picker>
                </v-menu>
            </v-col>
        </v-row>

        <!-- Download Cbks -->
        <v-row>
            <v-col v-if="this.$store.getters.role" align="end" class="py-0">
                <v-btn
                    small
                    dark
                    color="primary"
                    class="mb-3"
                    @click="download"
                    ><v-icon>mdi-arrow-down-bold</v-icon>
                </v-btn>
            </v-col>
        </v-row>
        
        <v-col md="12" class="pt-0 px-0 caption stats justify-end">
            <v-btn x-small color="primary" dark tile class="elevation-0">
                <v-icon class="px-1" small color="white">mdi-sigma</v-icon>Total number of chargebacks: {{ total }}
            </v-btn>
        </v-col>
       
        <!-- Chargebacks Table -->
        <v-data-table
            :headers="tableHeaders"
            :items="tableData"
            :items-per-page="10"
            :loading="loading"
            class="elevation-1"
        >
            <template v-slot:item.posted_date="{ item }">
                {{ formatPostDate(item.posted_date) }}
            </template>
            <template v-slot:item.status="{ item }">
                <span v-if="item.status == 0">
                    Open
                </span>
                <span v-else-if="item.status == 1">
                    Defended
                </span>
            </template>
            <template v-slot:item.evidence="{ item }">
                <v-btn
                    v-if="item.evidence"
                    x-small
                    color="primary"
                    dark
                    @click="downloadEvidence(item)"
                >
                Download
                </v-btn>
            </template>
            <template v-slot:item.merge="{ item }">
                <v-btn
                    v-if="item.merge"
                    x-small
                    color="primary"
                    dark
                    @click="downloadDefence(item)"
                >
                Download
                </v-btn>
            </template>
            
            <template v-slot:item.action="{ item }">
                <!-- Defending -->
                <v-menu v-if="(item.risk_type == 'CBK1' || item.risk_type == 'CBK2') && item.address1" bottom offset-y>
                    <template v-slot:activator="{ on }">
                        <v-btn 
                            text
                            icon 
                            color="#757575"
                            v-on="on"
                            ><v-icon color="#757575" size="22">
                                mdi-menu
                            </v-icon>
                        </v-btn>
                    </template>

                    <v-list>
                        <v-list-item @click="addReplaceEvidence(item)" :disabled="item.status || !item.defender">
                            <v-list-item-title>
                                <v-icon small class="pr-3" color="warning">mdi-file-document-multiple-outline</v-icon>Add/Replace Evidence
                            </v-list-item-title>
                        </v-list-item>
                        <!-- <v-list-item @click="createDefenceFile(item)" :disabled="!item.evidence || item.defence">
                            <v-list-item-title>
                                <v-icon small class="pr-3" color="primary">mdi-flag</v-icon>Generate Defence File
                            </v-list-item-title>
                        </v-list-item> -->
                        <v-list-item @click="defend(item)" :disabled="(!item.evidence && !item.merge) || item.status">
                            <v-list-item-title>
                                <v-icon small class="pr-3" color="success">mdi-shield-check</v-icon>Defend
                            </v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="removeEvidence(item)" :disabled="(!item.evidence && !item.merge) || item.status">
                            <v-list-item-title>
                                <v-icon small class="pr-3" color="error">mdi-trash-can-outline</v-icon>Remove Evidence
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <!-- Refunding -->
                <v-menu v-else-if="(item.risk_type == 'TC40' || item.risk_type == 'SAFE')" bottom offset-y>
                    <template v-slot:activator="{ on }">
                        <v-btn 
                            text
                            icon 
                            color="#757575"
                            v-on="on"
                            ><v-icon color="#757575" size="22">
                                mdi-menu
                            </v-icon>
                        </v-btn>
                    </template>

                    <v-list>
                        <v-list-item @click="refund(item)" :disabled="!item.defender">
                            <v-list-item-title>
                                <v-icon small class="pr-3" color="warning">mdi-credit-card-refund-outline</v-icon>Refund
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>
        </v-data-table>

        <!-- File -->
        <input type="file" id="file" name="file" @change="selectFile">

        <!-- Snackbar -->
        <v-snackbar
            v-model="snackbar"
            color="primary"
            timeout="8000"
            bottom
        >
            {{ snackText }}
        </v-snackbar>
    </v-container>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
    data() {
        return {
            tableHeaders: [{ text: 'Post Date', value: 'posted_date', sortable: false, width: 120 },
                           { text: 'ARN', value: 'ARN', sortable: false },
                           { text: 'Case Ref', value:'case_id', sortable: false, width: 120 },
                           { text: 'Reason Code', value:'reason_code', sortable: false, align: 'center', width: 120 },
                           { text: 'Clearing Institute', value: 'clearingInstituteName', sortable: false, width: 150 },
                           { text: 'Merchant ID', value: 'merchantId', sortable: false, width: 120 },
                           { text: 'Type', value: 'risk_type', sortable: false },
                           { text: 'Amount', value: 'amount', sortable: false, align: 'center' },
                           { text: 'Currency', value: 'currency', sortable: false, align: 'center' },
                           { text: 'UUID', value: 'uuid', sortable: false },
                           { text: 'Merchant', value: 'merchantName', sortable: false, width: 150 },
                           { text: 'Channel', value: 'channelName', sortable: false, width: 200 },
                           { text: 'Card Holder', value: 'holder', sortable: false, width: 150 },
                           { text: 'Card No.', value: 'card', sortable: false },
                           { text: 'Rep Date', value: 'rep_post_date', sortabe: false, width: 120 },
                           { text: 'Defence Status', value: 'status', sortable: false, align: 'center', width: 120 },
                           { text: 'Evidence', value: 'evidence', sortable: false, align: 'center' },
                           { text: 'Defence', value: 'merge', sortable: false, align: 'center' },
                           { text: 'Action', value: 'action', sortable: false }],
            tableData: [],
            merchants: [],
            fromDateModal: false,
            toDateModal: false,
            clearingInstitutes: [{ value: 'Bambora (via Evry)', text: 'Bambora (via Evry)' },
                                 { value: 'BSCardServices', text: 'BSCardService' },
                                 { value: 'Concardis', text: 'Concardis' },
                                 { value: 'Optimalpayments', text: 'Optimalpayments' },
                                 { value: 'Securetrading_Omnipay', text: 'Securetrading_Omnipay' },
                                 { value: 'Worldline', text: 'Worldline' }],
            cbkTypes: [{ value: 'CBK1', text: 'First Chargeback' },
                       { value: 'CBK2', text: 'Second Chargeback' },
                       { value: 'RRQ', text: 'Retrieval Request' },
                       { value: 'TC40', text: 'Pre Risk TC40' },
                       { value: 'REP', text: 'Represented' },
                       { value: 'PREA', text: 'Pre Arbitration' },
                       { value: 'LOSS', text: 'Lost Representment' },
                       { value: 'SAFE', text: 'Pre Risk SAFE' }],
            repOptions: [{ value: null, text: 'Not Represented'}, { value: 1, text: 'Represented'}],
            search: { merchant: null,
                      clearingInstitute: null,
                      arn: null,
                      uuid: null,
                      fromDate: moment().startOf('month').format('YYYY-MM-DD'),
                      toDate: new Date().toISOString().substr(0, 10)
            },
            loading: false,
            item: null,
            snackbar: false,
            snackText: null
        }
    },
    beforeMount() {
        this.getChargebacks();
    },
    computed: {
        
    },
    methods: {
        getChargebacks() {
            this.master();
            this.loading = true;
            
        },
        master() {
            axios({
            method: 'post',
            url: 'get-risk',
            data: { search: this.search },
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                // console.log(response.data);
                this.tableData = response.data;
                this.loading = false;
                this.total = response.data.length;

                let arr = [];
                // let acq = [];
                response.data.map(item => {
                    if(item.merchantUuid) {
                        arr.push({ value: item.merchantUuid, text: item.merchantName });
                    }
                    // acq.push(item.clearingInstituteName);
                });
                this.merchants = arr.reduce((unique, o) => {
                    if(!unique.some(obj => obj.label === o.label && obj.value === o.value && o.value)) {
                    unique.push(o);
                    }
                    return unique;
                },[]);

                // this.clearingInstitutes = acq.filter((item, i, ar) => ar.indexOf(item) === i);
                this.merchants.sort((a, b) => a?.text.localeCompare(b?.text));
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        formatPostDate(date) {
            return moment(date).format('YYYY-MM-DD');
        },
        handleDateFilters() {
            if(this.search.toDate < this.search.fromDate) {
                this.search.fromDate = this.search.toDate;
            }
            this.getChargebacks();
        },
        addReplaceEvidence(item) {
            this.item = item;
            document.getElementById("file").click();
        },
        selectFile(e) {
            this.file = e.target.files[0];

            const data = new FormData();
            data.append('evidence', e.target.files[0]);
            data.append('item', JSON.stringify(this.item));

            axios({
            method: 'post',
            url: 'add-evidence',
            data: data
            })
            .then(response => {
                console.log(response.data);
                this.item.defence_id = response.data['defenceId'];
                this.item.evidence = response.data['evidence'];
                this.createDefenceFile(this.item);

                console.log(response.status);
            })
            .catch(e => {
                if(e.response.status == 422) {
                    this.snackText = 'Invalid file type - Please upload a .pdf document';
                    this.snackbar = true;
                }
                this.errors.push(e)
            })
        },
        createDefenceFile(item) {
            axios({
            method: 'post',
            url: 'create-defence-file',
            data: { cbk: item },
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                item.merge = response.data;
                item.status = 0;
                this.snackText = 'Successfully added evidence & created defence file';
                this.snackbar = true;
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        downloadDefence(item) {
            axios({
            method: 'post',
            url: 'download-defence',
            data: { cbk: item },
            responseType: 'blob',
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${item.merge}`);
                document.body.appendChild(link);
                link.click();
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        downloadEvidence(item) {
            axios({
            method: 'post',
            url: 'download-evidence',
            data: { cbk: item },
            responseType: 'blob',
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${item.evidence}`);
                document.body.appendChild(link);
                link.click();
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        removeEvidence(item) {
            axios({
            method: 'post',
            url: 'remove-evidence',
            data: item,
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                item.defence_id = null;
                item.evidence = null;
                item.defence = null;
                item.merge = null;
                item.status = null;
                
                this.snackText = 'Successfully removed evidence & defence file';
                this.snackbar = true;
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        defend(item) {
            axios({
            method: 'post',
            url: 'defend-cbk',
            data: item,
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                console.log(response.data);
                item.status = 1;
                
                this.snackText = 'Defence form successfully sent to acquirer';
                this.snackbar = true;
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        refund(item) {
            axios({
            method: 'post',
            url: 'cbk-refund',
            data: item,
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                if(response.data.result.code == '000.000.000') {
                    this.snackText = 'Refund has been successfully processed';
                }
                else {
                    this.snackText = response.data.result.code + ': ' + response.data.result.description;
                }
                this.snackbar = true;
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        download() {
            if(this.$store.state.user.user_role.id <= 3) {
                axios({
                    method: 'post',
                    url: 'download-chargedef-stats',  
                    data: { search: this.search },              
                    responseType: 'blob',
                    config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                    })
                    .then(response => {
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'chargebacks-report.csv');
                        document.body.appendChild(link);
                        link.click();
                    })
                    .catch(e => {
                        this.errors.push(e)
                })
            }
            else {
                axios({
                    method: 'post',
                    url: 'download-chargedef-stats',  
                    data: { userId: this.$store.state.user.id, search: this.search },              
                    responseType: 'blob',
                    config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                    })
                    .then(response => {
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'chargebacks-report.csv');
                        document.body.appendChild(link);
                        link.click();
                    })
                    .catch(e => {
                        this.errors.push(e)
                })
            }
        }
    }
}
</script>

<style>
    .v-data-table td {
        font-size: 0.6rem !important;
    }

    .stats {
        display: inline-flex;
        vertical-align: middle;
        align-items: center;
    }

    #file {
        display: none;
    }
</style>